import React, { useEffect, useState } from "react";
import "./UserActivityPage.scss";
import getDataFromApi from "../../components/getDataFromApi";
import Sidenav from "../navbar/Sidenav";
import { useLocation, useNavigate } from "react-router-dom";
import TitleHeader from "../project/header/TitleHeader";
import { Trash2Icon, CheckIcon } from "lucide-react";
import { ApiTransferIcon, CheckCircleIcon, CloseIcon } from "../Common/SvgIcons/WorkflowIcons";
import PathBreadcrumb from "../../components/PathBreadcrumb/PathBreadcrumb";
import { Button } from "reactstrap";

const BASE_URL = `${window["ENV"].API_URL}`;

const EXPIRY_PRESETS = {
  DAYS_30: "30",
  DAYS_60: "60",
  PLAN_EXPIRES: "plan_expires",
};

const UserActivityPage = ({
  embedded = false,
  userId: propUserId,
  user: propUser,
} = {}) => {
  const activeTab = "list";
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const scopedUserId =
    propUserId ||
    location.state?.id ||
    location.state?.user?.id ||
    location.state?.user_id ||
    "";
  const scopedUser = propUser || location.state?.user || null;
  const fromSettings = Boolean(location.state?.fromSettings) || embedded;
  const viewFrom = location.state?.viewFrom || (embedded ? "profile_dashboard" : undefined);
  const currentUserId = localStorage.getItem("user_id");
  const isOwnKeys =
    scopedUserId && String(scopedUserId) === String(currentUserId);
  const [expiryPreset, setExpiryPreset] = useState(EXPIRY_PRESETS.DAYS_30);

  const [listForm, setListForm] = useState({
    is_active: "",
    scope: "",
    name: "",
    user_id: scopedUserId,
    sort_by: "created_at",
    sort_order: "desc",
    page: 1,
    page_size: 20,
  });

  const [createForm, setCreateForm] = useState({
    name: "",
    description: "",
    scopes: [],
    expires_in_days: 30,
  });

  const [listSearchBy, setListSearchBy] = useState("name");

  const [settingsForm, setSettingsForm] = useState({
    api_keys_enabled: true,
    max_keys_per_user: 10,
    allowed_scopes: ["models", "projects", "datasets", "evaluations", "*"],
    max_expiry_days: 365,
  });

  const [responses, setResponses] = useState({
    list: null,
    create: null,
    deleteKey: null,
    getSettings: null,
    updateSettings: null,
  });

  const [loading, setLoading] = useState({
    list: false,
    create: false,
    deleteKey: false,
    getSettings: false,
    updateSettings: false,
  });

  const handleApi = async (key, url, method = "GET", body = null) => {
    try {
      setLoading((prev) => ({ ...prev, [key]: true }));

      const data = await getDataFromApi(url, method, body);

      setResponses((prev) => ({
        ...prev,
        [key]: {
          status: 200,
          ok: true,
          data,
        },
      }));
    } catch (error) {
      setResponses((prev) => ({
        ...prev,
        [key]: {
          status: error.status || "ERROR",
          ok: false,
          data: { message: error.message },
        },
      }));
    } finally {
      setLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      submitListKeys();
    }, 350);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    listForm.is_active,
    listForm.scope,
    listForm.name,
    listForm.user_id,
    listForm.sort_by,
    listForm.sort_order,
    listForm.page,
    listForm.page_size,
    scopedUserId,
  ]);

  useEffect(() => {
    const maxExpiryDays = Number(settingsForm.max_expiry_days) || 1;
    const nextExpiresInDays =
      expiryPreset === EXPIRY_PRESETS.DAYS_30
        ? 30
        : expiryPreset === EXPIRY_PRESETS.DAYS_60
          ? 60
          : maxExpiryDays;

    setCreateForm((prev) => ({
      ...prev,
      expires_in_days: nextExpiresInDays,
    }));
  }, [expiryPreset, settingsForm.max_expiry_days]);

  const submitListKeys = (e) => {
    if (e?.preventDefault) e.preventDefault();

    const params = new URLSearchParams();

    const userIdFilter = listForm.user_id || scopedUserId;

    if (listForm.is_active !== "") params.append("is_active", listForm.is_active);
    if (listForm.scope) params.append("scope", listForm.scope);
    if (listForm.name) params.append("name", listForm.name);
    if (userIdFilter) params.append("user_id", userIdFilter);
    if (listForm.sort_by) params.append("sort_by", listForm.sort_by);
    if (listForm.sort_order) params.append("sort_order", listForm.sort_order);
    if (listForm.page) params.append("page", listForm.page);
    if (listForm.page_size) params.append("page_size", listForm.page_size);

    handleApi("list", `${BASE_URL}/api_keys?${params.toString()}`, "GET");
  };

  const submitCreateKey = (e) => {
    e.preventDefault();
    if (!createForm.scopes.length) return;
    setCopiedKey(false);

    const payload = {
      name: createForm.name,
      description: createForm.description,
      scopes: createForm.scopes,
      expires_in_days: Number(createForm.expires_in_days),
      ...(scopedUserId ? { user_id: scopedUserId } : {}),
    };

    handleApi("create", `${BASE_URL}/api_keys`, "POST", payload);
  };

  useEffect(() => {
    if (!responses.create?.ok) return;
    submitListKeys();
  }, [responses.create]);

  const handleDeleteKey = async (keyId) => {
    if (!keyId) return;

    try {
      await handleApi("deleteKey", `${BASE_URL}/api_keys/${keyId}`, "DELETE");

      // refresh list after delete
      submitListKeys();
    } catch (error) {
      console.error("Failed to delete API key:", error);
    }
  };

  const submitGetSettings = () => {
    handleApi("getSettings", `${BASE_URL}/api_keys/settings`, "GET");
  };

  const submitUpdateSettings = (e) => {
    e.preventDefault();

    const payload = {
      api_keys_enabled: settingsForm.api_keys_enabled,
      max_keys_per_user: Number(settingsForm.max_keys_per_user),
      allowed_scopes: settingsForm.allowed_scopes,
      max_expiry_days: Number(settingsForm.max_expiry_days),
    };

    handleApi("updateSettings", `${BASE_URL}/api_keys/settings`, "PUT", payload);
  };

  const AVAILABLE_SCOPES = ["models", "projects", "datasets", "evaluations", "annotations"];

  const handleCreateScopeToggle = (scope) => {
    setCreateForm((prev) => {
      const allIndividualScopes = AVAILABLE_SCOPES.filter((item) => item !== "*");
      const hasWildcard = prev.scopes.includes("*");

      if (scope === "*") {
        return {
          ...prev,
          scopes: hasWildcard ? [] : [...AVAILABLE_SCOPES],
        };
      }

      const exists = prev.scopes.includes(scope);

      let nextScopes = exists
        ? prev.scopes.filter((item) => item !== scope && item !== "*")
        : [...prev.scopes.filter((item) => item !== "*"), scope];

      const hasAllIndividuals = allIndividualScopes.every((item) =>
        nextScopes.includes(item)
      );

      if (hasAllIndividuals) {
        nextScopes = [...AVAILABLE_SCOPES];
      }

      return {
        ...prev,
        scopes: nextScopes,
      };
    });
  };

  const handleRemoveCreateScope = (scope) => {
    setCreateForm((prev) => ({
      ...prev,
      scopes: prev.scopes.filter((item) => item !== scope && item !== "*"),
    }));
  };

  const handleAllowedScopeChange = (scope) => {
    setSettingsForm((prev) => {
      const exists = prev.allowed_scopes.includes(scope);
      return {
        ...prev,
        allowed_scopes: exists
          ? prev.allowed_scopes.filter((s) => s !== scope)
          : [...prev.allowed_scopes, scope],
      };
    });
  };

  const handleCopyCreatedKey = async () => {
    const apiKey = responses?.create?.data?.key;
    if (!apiKey) return;

    try {
      await navigator.clipboard.writeText(apiKey);
      setCopiedKey(true);

      setTimeout(() => {
        setCopiedKey(false);
      }, 2000);
    } catch (error) {
      console.error("Failed to copy API key:", error);
    }
  };

  const renderResponse = (response) => {
    if (!response) return null;

    return (
      <div className="response-box">
        <div className={`response-status ${response.ok ? "success" : "error"}`}>
          Status: {response.status}
        </div>
        <pre>{JSON.stringify(response.data, null, 2)}</pre>
      </div>
    );
  };

  const formatDate = (value) => {
    if (!value) return "-";
    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  };

  const clearListFilters = () => {
    setListForm({
      is_active: "",
      scope: "",
      name: "",
      user_id: scopedUserId,
      sort_by: "created_at",
      sort_order: "desc",
      page: 1,
      page_size: 20,
    });
    setListSearchBy("name");
  };

  const hasActiveFilters =
    listForm.is_active !== "" ||
    listForm.scope ||
    listForm.name ||
    (!scopedUserId && listForm.user_id);

  const listSearchValue =
    listSearchBy === "name"
      ? listForm.name
      : listSearchBy === "scope"
        ? listForm.scope
        : listForm.user_id;

  const handleListSearchChange = (value) => {
    setListForm((prev) => ({
      ...prev,
      name: listSearchBy === "name" ? value : "",
      scope: listSearchBy === "scope" ? value : "",
      user_id:
        scopedUserId ||
        (listSearchBy === "user_id" ? value : ""),
      page: 1,
    }));
  };

  const handleListSearchTypeChange = (type) => {
    setListSearchBy(type);
    setListForm((prev) => ({
      ...prev,
      name: "",
      scope: "",
      user_id: scopedUserId || (type === "user_id" ? prev.user_id : ""),
      page: 1,
    }));
  };

  const renderCreateKeyResult = () => {
    const response = responses.create;

    if (!response) return null;

    if (!response.ok) {
      return (
        <div className="response-box">
          <div className="response-status error">Status: {response.status}</div>
          <pre>{JSON.stringify(response.data, null, 2)}</pre>
        </div>
      );
    }

    const apiKey = response?.data?.key;

    if (!apiKey) {
      return (
        <div className="response-box">
          <div className="response-status success">Status: {response.status}</div>
          <pre>{JSON.stringify(response.data, null, 2)}</pre>
        </div>
      );
    }

    return (
      <div className="created-key-panel">
        <div className="created-key-panel__header">
          <div>
            <h4>API Key Created</h4>
            <p>Copy this code now. It will not be available again after this.</p>
          </div>

          <span className="created-key-badge">One-time visible</span>
        </div>

        <div className="created-key-code-wrap">
          <code className="created-key-code">{apiKey}</code>

          <button
            type="button"
            className="copy-code-btn"
            onClick={handleCopyCreatedKey}
          >
            {copiedKey ? "Copied!" : "Copy Code"}
          </button>
        </div>

        <div className="created-key-note">
          Note: Save this key in a secure place before leaving this page.
        </div>
      </div>
    );
  };

  const renderApiKeyTable = () => {
    const response = responses.list;

    if (loading.list && !response) {
      return (
        <div className="state-panel " aria-busy="true">
          <div className="state-panel__icon">
            <span className="spinner" aria-hidden="true" />
          </div>
          <h4 className="text">Loading API keys…</h4>
          <p className="text">Fetching keys for the current filters.</p>
        </div>
      );
    }

    if (!response) return null;

    if (!response.ok) {
      const message =
        response?.data?.message ||
        response?.data?.detail ||
        "We couldn’t fetch API keys right now.";

      return (
        <div className="state-panel state-panel--error" role="alert">
          <div className="state-panel__icon" aria-hidden="true">
            ⚠️
          </div>
          <h4 className="text">Unable to load API keys</h4>
          <p className="text-secondary mb-0">
            {message}
          </p>
        </div>
      );
    }

    const keys = response?.data?.keys || [];

    if (!keys.length) {
      return (
        <div className="empty-state">
          <div className="empty-state__icon">🔑</div>
          <h4 className="text">No API keys found</h4>
          <p className="text-secondary">Try adjusting the filters, or create a new key.</p>

          <div className="empty-state__actions">
            <button
              type="button"
              className="primary-btn empty-state__btn"
              onClick={() => openCreatePanel()}
            >
              Create API Key
            </button>

            {hasActiveFilters && (
              <button
                type="button"
                className="ghost-btn empty-state__btn"
                onClick={clearListFilters}
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className={`api-key-table-wrap training-table-wrap mt-3 ${loading.list ? "is-refreshing" : ""}`}>
        <table className="training-table api-key-training-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Status</th>
              <th>Scopes</th>
              {!scopedUserId && <th>User</th>}
              <th>Last Used</th>
              <th>Created</th>
              <th>Expires</th>
              <th></th>
            </tr>
          </thead>

          <tbody>
            {keys.map((item) => (
              <tr key={item.id}>
                <td>
                  <div className="training-table-primary">
                    <div>
                      <strong>{item.name || "Unnamed Key"}</strong>
                      <span>{item.description || "No description"}</span>
                      {item.key_prefix ? (
                        <span className="api-key-table-prefix">
                          Prefix <code>{item.key_prefix}</code>
                        </span>
                      ) : null}
                    </div>
                  </div>
                </td>

                <td>
                  <span
                    className={`api-key-status-text ${
                      item.is_active ? "is-active" : "is-inactive"
                    }`}
                  >
                    {item.is_active ? "Active" : "Inactive"}
                  </span>
                </td>

                <td>
                  <div className="scopes-scroll">
                    <div className="scope-chip-list scope-chip-list--nowrap">
                      {(item.scopes || []).length ? (
                        item.scopes.map((scope) => (
                          <span className="api-key-scope-chip text-capitalize" key={scope}>
                            {scope}
                          </span>
                        ))
                      ) : (
                        <span className="api-key-scope-chip is-empty">No scopes</span>
                      )}
                    </div>
                  </div>
                </td>

                {!scopedUserId && (
                  <td>
                    <div className="table-user-cell">
                      <div>{item.user_email || "-"}</div>
                    </div>
                  </td>
                )}

                <td>{formatDate(item.last_used_at)}</td>
                <td>{formatDate(item.created_at)}</td>
                <td>{formatDate(item.expires_at)}</td>
                <td>
                  {item.is_active ? (
                    <button
                      type="button"
                      className="api-key-row-action"
                      onClick={() => handleDeleteKey(item.id)}
                      aria-label={`Delete ${item.name || "API key"}`}
                    >
                      <Trash2Icon className="wf-icon-small" />
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const resetCreatePanel = () => {
    setCreateForm({
      name: "",
      description: "",
      scopes: [],
      expires_in_days: 30,
    });
    setExpiryPreset(EXPIRY_PRESETS.DAYS_30);
    setCopiedKey(false);
    setResponses((prev) => ({ ...prev, create: null }));
  };

  const openCreatePanel = () => {
    resetCreatePanel();
    setShowCreatePanel(true);
  };

  const renderCreateKeyForm = () => (
    <>
      <form onSubmit={submitCreateKey} className="create-key-form open-form h-100 d-flex flex-column">
        <div className="create-key-fields flex-fill">
          <div className="dataset-flow-form-field">
            <label className="form-label" htmlFor="api-key-name">
              Name <span className="text-danger">*</span>
            </label>
            <input
              id="api-key-name"
              type="text"
              className="form-control"
              value={createForm.name}
              onChange={(e) =>
                setCreateForm({ ...createForm, name: e.target.value })
              }
              placeholder="Enter key name"
              required
            />
          </div>

          <div className="dataset-flow-form-field">
            <label className="form-label">
              Expires In Days <span className="text-danger">*</span>
            </label>
            <div
              className="container-card api-key-expiry-options"
              role="radiogroup"
              aria-label="Expires in days"
            >
              {[
                { value: EXPIRY_PRESETS.DAYS_30, label: "30 Days" },
                { value: EXPIRY_PRESETS.DAYS_60, label: "60 Days" },
                { value: EXPIRY_PRESETS.PLAN_EXPIRES, label: "Till plan expires" },
              ].map((option) => (
                <label
                  key={option.value}
                  className="expiry-radio d-inline-flex align-items-center mb-0"
                  htmlFor={`api-key-expiry-${option.value}`}
                >
                  <input
                    id={`api-key-expiry-${option.value}`}
                    type="radio"
                    className="form-check-input"
                    name="expires_in_days"
                    value={option.value}
                    checked={expiryPreset === option.value}
                    onChange={() => setExpiryPreset(option.value)}
                    required={option.value === EXPIRY_PRESETS.DAYS_30}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="dataset-flow-form-field">
            <label className="form-label" htmlFor="api-key-description">
              Description
            </label>
            <input
              id="api-key-description"
              type="text"
              className="form-control"
              value={createForm.description}
              onChange={(e) =>
                setCreateForm({ ...createForm, description: e.target.value })
              }
              placeholder="Short description"
            />
          </div>

          <div className="dataset-flow-form-field">
            <label className="form-label">
              Scopes <span className="text-danger">*</span>
            </label>
            <div className="d-flex flex-wrap gap-2">
              {AVAILABLE_SCOPES.map((scope) => {
                const selected = createForm.scopes.includes(scope);

                return (
                  <button
                    key={scope}
                    type="button"
                    className={`scope-select-chip ${selected ? "selected" : ""}`}
                    onClick={() => handleCreateScopeToggle(scope)}
                  >
                    <span>{scope}</span>
                    {selected && (
                      <CheckIcon className="wf-icon-small text-primary" />
                    )}
                  </button>
                );
              })}
            </div>
            <div className="form-help mb-0 mt-2">
              {createForm.scopes.length
                ? `${createForm.scopes.length} scope${createForm.scopes.length === 1 ? "" : "s"} selected`
                : "Please select at least one scope"}
            </div>
          </div>
        </div>

        <div className="api-key-drawer-actions dataset-footer">
          <div className="dataset-footer__actions d-flex justify-content-end w-100">
            <Button
              type="submit"
              color="primary"
              className="bg-main border-0 px-3"
              disabled={loading.create || !createForm.name.trim() || !createForm.scopes.length}
            >
              {loading.create ? "Creating..." : "Create API Key"}
            </Button>
          </div>
        </div>
      </form>

      {renderCreateKeyResult()}
    </>
  );

  return (
    <>
      <div className={embedded ? "api-key-management-embed w-100" : "mainmenu projects-content de-width"}>
        {!embedded && (
        <PathBreadcrumb
          onBack={() => {
            if (fromSettings || (isOwnKeys && viewFrom === "profile_dashboard")) {
              navigate("/admin/user-profile-edit", {
                state: {
                  id: scopedUserId,
                  user: scopedUser || location.state?.user,
                  viewFrom: "profile_dashboard",
                  showApiKeys: true,
                },
              });
              return;
            }
            navigate(-1);
          }}
          items={
            fromSettings || (isOwnKeys && viewFrom === "profile_dashboard")
              ? [
                  {
                    label: "Profile",
                    onClick: () =>
                      navigate("/user-profile", {
                        state: {
                          id: scopedUserId,
                          user: scopedUser || location.state?.user,
                          viewFrom: "profile_dashboard",
                        },
                      }),
                  },
                  {
                    label: "Settings",
                    onClick: () =>
                      navigate("/admin/user-profile-edit", {
                        state: {
                          id: scopedUserId,
                          user: scopedUser || location.state?.user,
                          viewFrom: "profile_dashboard",
                          showApiKeys: true,
                        },
                      }),
                  },
                  { label: "API Key Management" },
                ]
              : [
                  { label: "Admin", onClick: () => navigate("/admin") },
                  { label: "Users", onClick: () => navigate(-1) },
                  { label: "API Key Management" },
                ]
          }
        />
        )}
        <div className={`${embedded ? "mt-0" : "mt-4"} w-100 d-a flex-column flex-sm-row justify-content-between gap-3`}>
          {embedded ? (
            <div>
              <h4 className="m-0 fw-bold">API Key Management</h4>
              <p className="text-secondary mb-0 mt-1">
                Create and manage API keys for programmatic access to DeepEdge.
              </p>
            </div>
          ) : (
          <TitleHeader
            Icon={ApiTransferIcon}
            title1={'API key'}
            title2={' Management'}
            subtext={
              scopedUserId
                ? `Manage API keys for ${scopedUser?.email || location.state?.user?.email || "this user"}.`
                : `Manage API keys and tenant API key settings.`
            }
          />
          )}
          <Button
            color="primary px-3 py-2 btn-click bg-main border-0  btn-full-width"
            style={{ whiteSpace: "nowrap" }}
            onClick={openCreatePanel}
          >
            Create API Key
          </Button>
          {/* <div className="tab-bar">
           <button
            className={activeTab === "getSettings" ? "tab active" : "tab"}
            onClick={() => setActiveTab("getSettings")}
          >
            Get Settings
          </button>
          <button
            className={activeTab === "updateSettings" ? "tab active" : "tab"}
            onClick={() => setActiveTab("updateSettings")}
          >
            Update Settings
          </button> 
          </div> */}
        </div>


        {activeTab === "list" && (
          <div className={embedded ? "api-key-list-embed" : ""}>
            <div className="filters-wrap">
              {!embedded && (
                <div className="filters-topbar">
                  <div className="filters-heading">
                    <h3 className="text mt-4">List API Keys</h3>
                    <p>Search and refine keys instantly with smart filters.</p>
                  </div>
                </div>
              )}

              <div className="api-key-filters">
                <div className="api-key-filters__search">
                  <input
                    type="text"
                    className="text"
                    value={listForm.name}
                    onChange={(e) =>
                      setListForm({
                        ...listForm,
                        name: e.target.value,
                        scope: "",
                        page: 1,
                      })
                    }
                    placeholder="Search by key name..."
                    aria-label="Search by key name"
                  />
                </div>

                <div className="api-key-filters__controls">
                  <div className="api-key-filters__status">
                    <div className="status-switches status-switches--inline" role="group" aria-label="Filter by status">
                      <button
                        type="button"
                        className={`status-pill ${listForm.is_active === "" ? "active" : ""}`}
                        onClick={() => setListForm({ ...listForm, is_active: "", page: 1 })}
                      >
                        All
                      </button>
                      <button
                        type="button"
                        className={`status-pill ${listForm.is_active === "true" ? "active" : ""}`}
                        onClick={() =>
                          setListForm({ ...listForm, is_active: "true", page: 1 })
                        }
                      >
                        Active
                      </button>
                      <button
                        type="button"
                        className={`status-pill ${listForm.is_active === "false" ? "active" : ""}`}
                        onClick={() =>
                          setListForm({ ...listForm, is_active: "false", page: 1 })
                        }
                      >
                        Inactive
                      </button>
                    </div>
                  </div>

                  <div className="api-key-filters__sort">
                    <select
                      className="text"
                      value={`${listForm.sort_by}:${listForm.sort_order}`}
                      onChange={(e) => {
                        const [sort_by, sort_order] = e.target.value.split(":");
                        setListForm({ ...listForm, sort_by, sort_order, page: 1 });
                      }}
                      aria-label="Sort API keys"
                    >
                      <option value="created_at:desc">Newest first</option>
                      <option value="created_at:asc">Oldest first</option>
                      <option value="name:asc">Name A–Z</option>
                      <option value="name:desc">Name Z–A</option>
                      <option value="expires_at:asc">Expires soon</option>
                      <option value="last_used_at:desc">Recently used</option>
                    </select>
                  </div>
                </div>

                {(listForm.name || listForm.is_active !== "") && (
                  <div className="api-key-filters__reset">
                    <button
                      type="button"
                      className="plain-button2 text-danger p-0"
                      onClick={clearListFilters}
                    >
                      Reset
                    </button>
                  </div>
                )}
              </div>
            </div>

            {renderApiKeyTable()}
          </div>
        )}

        {activeTab === "getSettings" && (
          <div className="form-card">
            <h3 className="text">Get Tenant API Key Settings</h3>
            <p className="text-secondary">
              Fetch current tenant API key settings.
            </p>

            <div className="form-actions">
              <button
                type="button"
                className="primary-btn"
                onClick={submitGetSettings}
                disabled={loading.getSettings}
              >
                {loading.getSettings ? "Loading..." : "Get Settings"}
              </button>
            </div>

            {renderResponse(responses.getSettings)}
          </div>
        )}

        {activeTab === "updateSettings" && (
          <div className="form-card">
            <h3>Update Tenant API Key Settings</h3>
            <form onSubmit={submitUpdateSettings} className="form-grid">
              <div className="form-group">
                <label>API Keys Enabled</label>
                <select
                  value={String(settingsForm.api_keys_enabled)}
                  onChange={(e) =>
                    setSettingsForm({
                      ...settingsForm,
                      api_keys_enabled: e.target.value === "true",
                    })
                  }
                >
                  <option value="true">true</option>
                  <option value="false">false</option>
                </select>
              </div>

              <div className="form-group">
                <label>Max Keys Per User</label>
                <input
                  type="number"
                  value={settingsForm.max_keys_per_user}
                  onChange={(e) =>
                    setSettingsForm({
                      ...settingsForm,
                      max_keys_per_user: e.target.value,
                    })
                  }
                />
              </div>

              <div className="form-group full-width">
                <label>Allowed Scopes</label>
                <div className="checkbox-group">
                  {["models", "projects", "datasets", "evaluations", "*"].map(
                    (scope) => (
                      <label key={scope} className="checkbox-item">
                        <input
                          type="checkbox"
                          checked={settingsForm.allowed_scopes.includes(scope)}
                          onChange={() => handleAllowedScopeChange(scope)}
                        />
                        {scope}
                      </label>
                    )
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Max Expiry Days</label>
                <input
                  type="number"
                  value={settingsForm.max_expiry_days}
                  onChange={(e) =>
                    setSettingsForm({
                      ...settingsForm,
                      max_expiry_days: e.target.value,
                    })
                  }
                />
              </div>

              <div className="form-actions full-width">
                <button
                  type="submit"
                  className="primary-btn"
                  disabled={loading.updateSettings}
                >
                  {loading.updateSettings ? "Updating..." : "Update Settings"}
                </button>
              </div>
            </form>

            {renderResponse(responses.updateSettings)}
          </div>
        )}
      </div>

      {showCreatePanel && (
        <div className="api-key-drawer-overlay" onMouseDown={() => setShowCreatePanel(false)}>
          <aside
            className="api-key-drawer-panel evaluate-model-ui dataset-flow-shell"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="api-key-drawer-header">
              <div className="d-flex gap-2 align-items-start w-100">
                <ApiTransferIcon className="wf-icon-blue text-white bg-primary fw-bold p-2 flex-shrink-0" />
                <div className="flex-fill min-w-0">
                  <div className="d-flex justify-content-between w-100 gap-2">
                    <h2 className="main-title mb-0">Create API Key</h2>
                    <button
                      type="button"
                      className="plain-button2 p-0"
                      onClick={() => setShowCreatePanel(false)}
                      aria-label="Close create API key panel"
                    >
                      <CloseIcon className="wf-icon text-secondary" />
                    </button>
                  </div>
                  <p className="sub-title mb-0">
                    Generate a new key and assign the scopes it can access.
                  </p>
                </div>
              </div>
            </div>

            <div className="api-key-drawer-body">
              {renderCreateKeyForm()}
            </div>
          </aside>
        </div>
      )}
    </>
  );
};

export default UserActivityPage;
